import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { KeyRound } from "lucide-react";
import { AlertBanner, Button, FormField, Heading, Icon, Text } from "@lendcord/concord";
import { useSession } from "@/app/session";
import { useChangePasswordMutation, useLogoutMutation } from "@/auth/api/auth.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { PASSWORD_REGEX } from "@/auth/validators";
import { AuthHeader } from "./components/AuthHeader";
import { BrandPanel } from "./components/BrandPanel";
import { PasswordInput } from "./components/PasswordInput";

// Rendered by ForcedPasswordChangeGate (see app/session) in place of every
// authenticated route's Outlet whenever the session carries
// mustChangePassword=true — an agent/admin onboarded this account with a
// generated password (see lendcord-core's OnboardingResponse.generatedPassword)
// and it must be rotated before anything else in the app is reachable.
// Deliberately mirrors AuthPage's brand-panel layout rather than living
// inside DashboardLayout — this gate sits *before* the app shell, not
// inside it.
export function ForcePasswordChangePage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { clearMustChangePassword, logout } = useSession();
  const [changePassword] = useChangePasswordMutation();
  const [logoutRequest] = useLogoutMutation();

  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    temporaryPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const nextErrors: typeof errors = {};
    if (!temporaryPassword) {
      nextErrors.temporaryPassword = intl.formatMessage({ id: "auth.forcePasswordChange.temporaryPasswordError" });
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      nextErrors.newPassword = intl.formatMessage({ id: "auth.forcePasswordChange.newPasswordError" });
    }
    if (confirmPassword !== newPassword) {
      nextErrors.confirmPassword = intl.formatMessage({ id: "auth.forcePasswordChange.confirmPasswordError" });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setFormError(undefined);
    setIsSubmitting(true);
    try {
      await changePassword({ oldPassword: temporaryPassword, newPassword }).unwrap();
      clearMustChangePassword();
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-072") {
        setErrors({
          temporaryPassword: intl.formatMessage({ id: "auth.forcePasswordChange.wrongTemporaryPasswordError" }),
        });
      } else {
        setFormError(intl.formatMessage({ id: "auth.forcePasswordChange.genericError" }));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await logoutRequest().unwrap();
    } catch {
      // Ignored — same as TopBar's handleLogout: the local session still
      // ends below regardless of whether server-side revocation succeeded.
    } finally {
      logout();
      navigate("/auth", { replace: true });
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <AuthHeader />

      <div className="flex flex-1 flex-col md:flex-row">
        <BrandPanel />

        <div className="flex flex-1 items-center justify-center px-6 py-10 md:px-12">
          <div className="w-full max-w-sm">
            <div className="mb-6 flex flex-col items-center text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-50">
                <Icon icon={KeyRound} size="lg" className="text-accent-600" />
              </div>
              <Heading level={2} className="mb-1">
                <FormattedMessage id="auth.forcePasswordChange.heading" />
              </Heading>
              <Text size="sm" variant="muted">
                <FormattedMessage id="auth.forcePasswordChange.subtitle" />
              </Text>
            </div>

            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
              <AlertBanner status="info">
                <FormattedMessage id="auth.forcePasswordChange.infoBanner" />
              </AlertBanner>

              {formError && <AlertBanner status="error">{formError}</AlertBanner>}

              <FormField
                label={intl.formatMessage({ id: "auth.forcePasswordChange.temporaryPasswordLabel" })}
                error={errors.temporaryPassword}
                required
              >
                <PasswordInput
                  value={temporaryPassword}
                  onChange={setTemporaryPassword}
                  placeholder={intl.formatMessage({ id: "auth.forcePasswordChange.temporaryPasswordPlaceholder" })}
                  autoComplete="current-password"
                />
              </FormField>

              <FormField
                label={intl.formatMessage({ id: "auth.forcePasswordChange.newPasswordLabel" })}
                error={errors.newPassword}
                required
                hint={
                  errors.newPassword
                    ? undefined
                    : intl.formatMessage({ id: "auth.forcePasswordChange.newPasswordHint" })
                }
              >
                <PasswordInput
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder={intl.formatMessage({ id: "auth.forcePasswordChange.newPasswordPlaceholder" })}
                  autoComplete="new-password"
                />
              </FormField>

              <FormField
                label={intl.formatMessage({ id: "auth.forcePasswordChange.confirmPasswordLabel" })}
                error={errors.confirmPassword}
                required
              >
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder={intl.formatMessage({ id: "auth.forcePasswordChange.confirmPasswordPlaceholder" })}
                  autoComplete="new-password"
                  disablePaste
                />
              </FormField>

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <FormattedMessage id="auth.forcePasswordChange.submittingButton" />
                ) : (
                  <FormattedMessage id="auth.forcePasswordChange.submitButton" />
                )}
              </Button>

              <Button type="button" variant="link" disabled={isLoggingOut} onClick={handleLogout} className="self-center">
                <FormattedMessage id="auth.forcePasswordChange.logoutLink" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
